import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { LoadingButton } from '@/components/loading-button.tsx'
import api from '@/api.ts'
import usePreferences from '@/usePreferences.ts'
import { SheetDescription } from '@/components/ui/sheet.tsx'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import LingoFlowIcon from '@/components/LingoFlowIcon.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Undo } from 'lucide-react'
import { SocialLogin } from '@capgo/capacitor-social-login'
import { toast } from 'sonner'
import useGlobalStore from '@/useGlobalStore.ts'
import { Capacitor } from '@capacitor/core'
import * as Sentry from '@sentry/react'

interface AuthDialogProps {
	open: boolean
	onSuccessfulLogin: () => void
	onDismissed: (dismissed: boolean) => void
	authMessage: string
	dismissible: boolean | undefined
}

const AuthDialog = ({ open, onSuccessfulLogin, onDismissed, authMessage, dismissible = true }: AuthDialogProps) => {
	const { t } = useTranslation()
	const { user, availableAppLanguages, appLanguageCode } = useGlobalStore()
	const { setItem: setPreferenceItem } = usePreferences()
	const [email, setEmail] = useState<string>('')
	const [password, setPassword] = useState<string>('')
	const [name, setName] = useState<string>('')
	const [activeLoading, setActiveLoading] = useState<string>('')
	const [errorMessage, setErrorMessage] = useState<string>('')
	const [emailMode, setEmailMode] = useState<string>('')

	const handleInteractionOutside = () => {
		if (dismissible) {
			onDismissed(true)
		}
	}

	const handleResetPassword = async () => {
		if (email === '') {
			toast(t('Enter your email'))
			return
		}
		setActiveLoading('reset-password')
		try {
			await api('/auth/reset-password', {
				method: 'POST',
				body: {
					email,
				},
			}).catch((error) => {
				Sentry.captureException(error)
				throw new Error(error.data?.message || 'Reset password failed')
			})
			toast(t('A password reset email has been sent to you. Check your inbox and possibly spam/junk folder.'))
			setErrorMessage('')
		} catch (error: any) {
			setErrorMessage(error.message)
		} finally {
			setActiveLoading('')
		}
	}

	const handleLogin = async () => {
		setActiveLoading('email-login')
		try {
			const { login_possible } = await api('/auth/login', {
				method: 'POST',
				body: {
					email,
				},
			}).catch((error) => {
				throw new Error(error.data?.message || 'Login failed')
			})
			if (login_possible) {
				setEmailMode('login')
			} else {
				setEmailMode('signup')
			}
			setErrorMessage('')
		} catch (error: any) {
			setErrorMessage(error.message)
		} finally {
			setActiveLoading('')
		}
	}

	const handleAttemptLogin = async () => {
		setActiveLoading('email-attempt-login')
		try {
			const { access_token, refresh_token } = await api('/auth/attempt-login', {
				method: 'POST',
				body: {
					email,
					password,
				},
			}).catch((error) => {
				throw new Error(error.data?.message || 'Login failed')
			})
			await setPreferenceItem(
				'user',
				JSON.stringify({
					access_token: access_token,
					refresh_token: refresh_token,
				}),
			)
			setErrorMessage('')
			onSuccessfulLogin()
		} catch (error: any) {
			setErrorMessage(error.message)
		} finally {
			setActiveLoading('')
		}
	}

	const handleSignup = async () => {
		setActiveLoading('email-signup')
		try {
			const { access_token, refresh_token } = await api('/auth/register', {
				method: 'POST',
				body: {
					name,
					email,
					password,
				},
				params: {
					headers: {
						'X-Trial-User-Id': user?.trial_expires_at !== null ? user?.id : null,
					},
				},
			}).catch((error) => {
				throw new Error(error.data?.message || 'Signup failed')
			})
			await setPreferenceItem(
				'user',
				JSON.stringify({
					access_token: access_token,
					refresh_token: refresh_token,
				}),
			)
			setErrorMessage('')
			onSuccessfulLogin()
		} catch (error: any) {
			setErrorMessage(error.message)
		} finally {
			setActiveLoading('')
		}
	}

	const handleAppleLogin = async () => {
		try {
			const { result } = await SocialLogin.login({
				provider: 'apple',
				options: {},
			})
			console.log(result)
			setActiveLoading('apple-login')
			const { access_token, refresh_token } = await api('/auth/apple/callback', {
				method: 'POST',
				body: result,
				params: {
					headers: {
						'X-Trial-User-Id': user?.trial_expires_at !== null ? user?.id : null,
					},
				},
			}).catch((error) => {
				Sentry.captureException(error)
				throw new Error(error.data?.message || 'Login/Signup failed')
			})
			await setPreferenceItem(
				'user',
				JSON.stringify({
					access_token: access_token,
					refresh_token: refresh_token,
				}),
			)
			setErrorMessage('')
			onSuccessfulLogin()
		} catch (error: any) {
			setErrorMessage(error.message)
		} finally {
			setActiveLoading('')
		}
	}

	const signOutGoogleOnIos = async () => {
		if (Capacitor.getPlatform() !== 'ios') return

		try {
			await SocialLogin.logout({
				provider: 'google',
			})
		} catch (error) {
			console.error('Failed to sign out from Google before login', error)
		}
	}

	const handleGoogleLogin = async () => {
		try {
			setActiveLoading('google-login')
			await signOutGoogleOnIos()
			const { result } = await SocialLogin.login({
				provider: 'google',
				options: {
					scopes: ['email', 'profile'],
				},
			})
			const { access_token, refresh_token } = await api('/auth/google/callback', {
				method: 'POST',
				body: result,
				params: {
					headers: {
						'X-Trial-User-Id': user?.trial_expires_at !== null ? user?.id : null,
					},
				},
			}).catch((error) => {
				Sentry.withScope((scope) => {
					scope.setExtra('responseData', error?.data)
					Sentry.captureException(error)
				})
				throw new Error(error.data?.message || 'Login/Signup failed')
			})
			await setPreferenceItem(
				'user',
				JSON.stringify({
					access_token: access_token,
					refresh_token: refresh_token,
				}),
			)
			setErrorMessage('')
			onSuccessfulLogin()
		} catch (error: any) {
			setErrorMessage(error.message)
		} finally {
			setActiveLoading('')
		}
	}

	const handleEnterPress = async (e: any) => {
		if (e.key === 'Enter') {
			if (emailMode === '') await handleLogin()
			else if (emailMode === 'signup') await handleSignup()
			else if (emailMode === 'login') await handleAttemptLogin()
		}
	}

	const handleBackPress = () => {
		setEmailMode('')
		setErrorMessage('')
	}

	// const resetForm = () => {
	// 	setEmail('')
	// 	setName('')
	// 	setPassword('')
	// 	setErrorMessage('')
	// }

	// useEffect(() => {
	// 	if (!open) {
	// 		resetForm()
	// 	}
	// }, [open])

	useEffect(() => {
		const providers: any = {
			google: {
				webClientId: import.meta.env.VITE_GOOGLE_AUTH_WEB_CLIENT_ID,
				iOSClientId: import.meta.env.VITE_GOOGLE_AUTH_IOS_CLIENT_ID,
				iOSServerClientId: import.meta.env.VITE_GOOGLE_AUTH_WEB_CLIENT_ID,
			},
		}

		if (Capacitor.getPlatform() === 'ios') {
			providers.apple = {}
		}

		SocialLogin.initialize(providers).then()
	}, [])

	return (
		<Dialog open={open}>
			<DialogContent
				dir={availableAppLanguages[appLanguageCode]?.dir}
				hideClose={true}
				onInteractOutside={handleInteractionOutside}>
				<VisuallyHidden.Root>
					<DialogTitle>Login or Sign up</DialogTitle>
					<SheetDescription>Create an account or log into your existing account</SheetDescription>
				</VisuallyHidden.Root>
				<div className="flex justify-center items-center mb-1">
					<LingoFlowIcon className="w-8 h-8" />
				</div>
				<p>
					{authMessage === ''
						? t('Create an account or log in in order to access all features of LingoFlow.')
						: authMessage}
				</p>
				<div className="flex flex-col gap-3">
					<Input
						type="email"
						placeholder={t('Enter your email')}
						value={email}
						onKeyUp={handleEnterPress}
						onChange={(e) => setEmail(e.target.value)}
						required
						tabIndex={-1}
					/>
					{emailMode === 'signup' && (
						<Input
							type="text"
							placeholder={t('Enter your name')}
							value={name}
							onKeyUp={handleEnterPress}
							onChange={(e) => setName(e.target.value)}
							required
						/>
					)}
					{emailMode !== '' && (
						<Input
							type="password"
							placeholder={t('Enter a password')}
							value={password}
							onKeyUp={handleEnterPress}
							onChange={(e) => setPassword(e.target.value)}
							required
						/>
					)}
					<div className="flex justify-center items-center">
						{emailMode === 'login' && (
							<Button onClick={handleBackPress} className="bg-white border-black text-black border-2">
								<Undo className="w-4 h-4" />
							</Button>
						)}
						<LoadingButton
							onClick={() =>
								emailMode === 'signup'
									? handleSignup()
									: emailMode === ''
										? handleLogin()
										: handleAttemptLogin()
							}
							type="submit"
							className="w-full bg-white border-black text-black border-2"
							loading={['email-login', 'email-attempt-login', 'email-signup'].includes(activeLoading)}>
							{t(emailMode === 'login' ? 'Log In' : 'Continue with email')}
						</LoadingButton>
					</div>
					{emailMode !== '' && (
						<div className="flex justify-evenly">
							{emailMode === 'signup' && (
								<div
									className="flex justify-center items-center text-sm mt-1 text-gray-400"
									onClick={() => setEmailMode('login')}>
									{t('Login instead')}
								</div>
							)}
							<div
								className="flex justify-center items-center text-sm mt-1 text-gray-400"
								onClick={handleResetPassword}>
								{t(activeLoading === 'reset-password' ? 'Please wait...' : 'Reset password')}
							</div>
						</div>
					)}
				</div>
				{errorMessage !== '' && <div className="text-sm dark:text-red-400 text-red-500">{errorMessage}</div>}
				<div className="relative my-2">
					<div className="absolute inset-0 flex items-center">
						<div className="w-full border-t dark:border-gray-300 border-gray-300" />
					</div>
					<div className="relative flex justify-center text-sm">
						<span className="bg-white dark:bg-background px-2 text-gray-500 dark:text-gray-300">
							{t('or continue with')}
						</span>
					</div>
				</div>
				<div className="flex flex-col gap-3">
					<LoadingButton
						onClick={handleGoogleLogin}
						loading={activeLoading === 'google-login'}
						variant="default"
						className="w-full flex items-center bg-white border-black text-black border-2">
						<img src="/google.svg" className="w-5 h-5 me-2" />
						{t('Continue with Google')}
					</LoadingButton>
					{Capacitor.getPlatform() === 'ios' && (
						<LoadingButton
							onClick={handleAppleLogin}
							loading={activeLoading === 'apple-login'}
							variant="default"
							className="w-full flex items-center bg-white border-black text-black border-2">
							<img src="/apple.svg" className="w-5 h-5 me-2" />
							{t('Continue with Apple')}
						</LoadingButton>
					)}
				</div>
				{/*<small>{t('terms_of_service')}</small>*/}
				<small>
					<Trans i18nKey="terms_of_service" components={{ a: <a /> }} />
				</small>
				{dismissible && (
					<div className="flex justify-center items-center text-sm mt-1 text-gray-400">
						<span className="cursor-pointer" onClick={() => onDismissed(dismissible)}>
							{t('Dismiss')}
						</span>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default AuthDialog
